import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  bug: {
    title: "Report a Bug",
    desc: "Tell us what went wrong. We'll attach the page you're on and your browser details.",
    subject: "Title", subjectPh: "e.g. Upload button does nothing",
    message: "Description", messagePh: "What happened? What did you expect?",
    done: "Thanks — we've received your report.",
  },
};

const inputCls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#001C60] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

export function SupportDialog({ kind, onClose }) {
  const [form, setForm] = useState({ subject: "", message: "", context: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  useEffect(() => { setForm({ subject: "", message: "", context: "" }); setState("idle"); setError(""); }, [kind]);
  const c = COPY[kind] || COPY.bug;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) { setError("Please fill in both fields."); return; }
    setState("sending"); setError("");
    try {
      await axios.post(`${API}/api/support/messages`, {
        kind, ...form, page: window.location.pathname + window.location.search, user_agent: navigator.userAgent,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem("compass_token")}` } });
      setState("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't send. Please try again.");
      setState("idle");
    }
  };

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={`support-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="text-[#001C60]">{c.title}</DialogTitle>
          {state !== "done" && <DialogDescription>{c.desc}</DialogDescription>}
        </DialogHeader>
        {state === "done" ? (
          <div className="py-6 text-center" data-testid="support-dialog-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <p className="text-sm font-medium text-gray-800">{c.done}</p>
            <button onClick={onClose} data-testid="support-dialog-close-btn"
              className="mt-5 px-4 py-2 text-sm font-medium bg-[#001C60] text-white rounded-lg hover:bg-blue-900 transition-colors">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label={c.subject}>
              <input value={form.subject} onChange={set("subject")} placeholder={c.subjectPh} maxLength={200}
                className={inputCls} data-testid="support-subject-input" />
            </Field>
            <Field label={c.message}>
              <textarea value={form.message} onChange={set("message")} placeholder={c.messagePh} rows={5}
                className={inputCls} data-testid="support-message-input" />
            </Field>
            {kind === "bug" && (
              <Field label="What were you doing? (optional)">
                <textarea value={form.context} onChange={set("context")} rows={2} placeholder="e.g. Filling out the Custody wizard"
                  className={inputCls} data-testid="support-context-input" />
              </Field>
            )}
            {error && <p className="text-xs text-red-500" data-testid="support-dialog-error">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} data-testid="support-cancel-btn"
                className="px-4 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">Cancel</button>
              <button type="submit" disabled={state === "sending"} data-testid="support-submit-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[#001C60] text-white rounded-lg hover:bg-blue-900 disabled:opacity-50 transition-colors">
                {state === "sending" && <Loader2 size={14} className="animate-spin" />} Send
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
